/**
 * Shows inherited workspace access and additional campus sharing.
 *
 * The workspace remains the single source of truth for its team; Vault grants
 * add recipients without giving those recipients workspace execution rights.
 */
import { useQuery } from '@tanstack/react-query'
import { KeyRound, Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { useServices } from '@/app-shell/services'
import { Button, Card, CardContent } from '@/components/ui'
import {
  getWorkspaceVaultStatus,
  listVaults,
} from '../services/vault-api'
import { VaultAccessManager } from './VaultAccessManager'

export function WorkspaceVaultAccessPanel({
  orgId,
  workspaceId,
}: {
  orgId: string
  workspaceId: string
}) {
  const api = useServices().getClient()
  const status = useQuery({
    queryKey: ['workspace-vault-status', orgId, workspaceId],
    queryFn: () => getWorkspaceVaultStatus(api, orgId, workspaceId),
  })
  const catalog = useQuery({
    queryKey: ['vault-catalog', orgId],
    queryFn: () => listVaults(api, orgId),
    enabled: status.data?.enabled === true,
  })
  const entry = catalog.data?.find(candidate => (
    candidate.pairing?.workspaceId === workspaceId
  ))
  if (status.isPending || catalog.isPending && status.data?.enabled) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading Vault access…
      </div>
    )
  }

  if (status.isError) {
    return <VaultAccessNotice title="Vault access is unavailable" />
  }

  if (!status.data.enabled) {
    return (
      <VaultAccessNotice
        title="Vault is off for this workspace"
        detail="Enable Vault in Workspace Settings before granting access."
      >
        <Button variant="outline" size="sm" asChild>
          <a href={`#/workspace-settings?org=${encodeURIComponent(orgId)}&ws=${encodeURIComponent(workspaceId)}&section=vault`}>
            Open Vault settings
          </a>
        </Button>
      </VaultAccessNotice>
    )
  }

  if (!entry) {
    return (
      <VaultAccessNotice
        title="Vault is enabled, but you do not have access"
        detail="Ask a workspace administrator to confirm your workspace membership."
      />
    )
  }

  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="flex items-start gap-3 border-b pb-5">
          <span className="rounded-lg bg-indigo-100 p-2.5 text-indigo-700">
            <KeyRound className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-xl font-semibold">Vault Access</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Your workspace team has automatic access to {entry.vault.name}.
              Additional recipients can view the Vault without running work in
              this workspace; editing permissions are set on individual folders
              or files.
            </p>
          </div>
        </div>
        <VaultAccessManager
          api={api}
          orgId={orgId}
          vaultId={entry.vault.vaultId}
          itemId="ROOT"
          readOnly={false}
        />
      </CardContent>
    </Card>
  )
}

function VaultAccessNotice({
  title,
  detail,
  children,
}: {
  title: string
  detail?: string
  children?: ReactNode
}) {
  return (
    <Card>
      <CardContent className="space-y-3 p-6">
        <h2 className="text-lg font-semibold">{title}</h2>
        {detail && <p className="text-sm text-muted-foreground">{detail}</p>}
        {children}
      </CardContent>
    </Card>
  )
}
